/*
 * Creators Page — Conversion-focused landing for game creators/hosts
 * AI advisor showcase, revenue potential, real-world examples, marketing materials
 */

import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  Rocket, Zap, Target, DollarSign, ArrowRight, CheckCircle2,
  ChefHat, Dumbbell, Palette, Music, Code, Camera, Brain,
  Gamepad2, TrendingUp, Users, Shield, Sparkles, BarChart3,
  Megaphone, Globe, Heart, Building2, GraduationCap, Play,
  MessageSquare, Wand2, Star, Clock,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { useState, useMemo } from "react";

const realWorldExamples = [
  {
    icon: ChefHat,
    title: "Food YouTuber — Cooking Challenge",
    scenario: "A food creator with 200K subscribers hosts a \"Best Homemade Ramen\" competition.",
    numbers: "500 fans enter at $5 each = $2,500 total. Host earns $375 (15%). Promoter earns $250 (10%). Winner takes $1,875.",
    takeaway: "The creator earns $375 for designing one game — more than most sponsored posts pay.",
    color: "text-gold",
  },
  {
    icon: Dumbbell,
    title: "Fitness Influencer — Transformation Battle",
    scenario: "A CrossFit coach runs a 30-day body transformation challenge for their community.",
    numbers: "200 members at $20 each = $4,000 total. Host earns $600 (15%). Top 5 split $2,800.",
    takeaway: "The coach earns $600 while building deeper community engagement than any workout video.",
    color: "text-neon",
  },
  {
    icon: Palette,
    title: "Art Teacher — Student Showcase",
    scenario: "An art instructor hosts a semester-end portfolio competition for students worldwide.",
    numbers: "300 entries at $10 each = $3,000 total. Host earns $450. Winners get real prize money for their art.",
    takeaway: "Students are more motivated than ever. The teacher earns while celebrating student talent.",
    color: "text-gold",
  },
  {
    icon: Heart,
    title: "Nonprofit — Charity Bake-Off",
    scenario: "A children's hospital runs a holiday baking competition. All host earnings go to the cause.",
    numbers: "1,000 bakers at $10 each = $10,000 total. $1,500 to the charity. $7,000 in prizes. Community loves it.",
    takeaway: "Fundraising through fun engagement, not donation fatigue. Participants get a chance to win too.",
    color: "text-neon",
  },
  {
    icon: Building2,
    title: "Corporate HR — Team Building",
    scenario: "A tech company runs a trivia tournament for 500 employees across 3 offices.",
    numbers: "Zero entry fee — company sponsors the prize pool. $5,000 in prizes. Engagement through the roof.",
    takeaway: "HR gets a turnkey engagement event. Employees compete across departments. Everyone talks about it for weeks.",
    color: "text-gold",
  },
  {
    icon: GraduationCap,
    title: "Coding Bootcamp — Hackathon",
    scenario: "A bootcamp hosts a 48-hour hackathon for current and former students.",
    numbers: "200 devs at $25 each = $5,000 total. Host earns $750. Best projects win prizes and portfolio pieces.",
    takeaway: "Students build real projects, compete for real money, and the bootcamp earns while showcasing talent.",
    color: "text-neon",
  },
];

const aiFeatures = [
  { icon: MessageSquare, title: "Conversational Game Design", desc: "Tell the AI about your audience, niche, and goals. It designs the perfect competition — game type, rules, entry fees, prize structure, duration, and scoring method." },
  { icon: TrendingUp, title: "Viral Score Analysis", desc: "Real-time scoring of your game's viral potential. The AI identifies what makes games spread and suggests improvements to maximize participation." },
  { icon: Shield, title: "Compliance Validation", desc: "Every game is automatically checked for skill-based legality. The AI ensures your competition is legal, ethical, and clearly skill-based — not gambling." },
  { icon: Megaphone, title: "Marketing Materials Generator", desc: "Auto-generated social media captions for Instagram, TikTok, and Twitter. Professional influencer outreach emails. Viral hooks optimized for sharing." },
  { icon: BarChart3, title: "Revenue Optimization", desc: "Smart entry fee and payout structures that maximize participation. The AI balances accessibility with prize pool size based on your audience demographics." },
  { icon: Sparkles, title: "Trending Event Templates", desc: "The AI scans trending events and generates ready-to-launch game templates. Turn any viral moment into a skill-based competition in seconds." },
];

const creatorBenefits = [
  "Earn up to 15% of every entry fee — set your own rate",
  "AI designs your entire game in under 10 minutes",
  "Auto-generated marketing materials ready to post",
  "No technical skills required — just describe your audience",
  "Built-in compliance checking for skill-based legality",
  "Real-time analytics on entries, revenue, and engagement",
  "Works for any niche — cooking, gaming, fitness, art, music, coding, trivia",
  "Viral mechanics built in — every submission is shareable",
];

export default function Creators() {
  const [monthlyGames, setMonthlyGames] = useState(4);
  const [avgPlayers, setAvgPlayers] = useState(200);
  const [avgFee, setAvgFee] = useState(10);
  const [hostPct, setHostPct] = useState(15);

  const revenue = useMemo(() => {
    const perGame = avgPlayers * avgFee * (hostPct / 100);
    const monthly = perGame * monthlyGames;
    const yearly = monthly * 12;
    return { perGame, monthly, yearly };
  }, [monthlyGames, avgPlayers, avgFee, hostPct]);

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-24 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/5 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/10 border border-gold/20 text-gold text-xs font-medium mb-6">
              <Target className="w-3.5 h-3.5" />
              For Creators
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight mb-6">
              Design Games That{" "}
              <span className="text-gradient-gold">Hook Players</span> and Generate{" "}
              <span className="text-gradient-neon">Massive Payouts</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              You know your audience. You know what they love. Now turn that knowledge into
              skill-based competitions that generate real revenue. Our AI advisor handles the
              game design, compliance, and marketing — you just bring the idea.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/create">
                <Button size="lg" className="bg-gold hover:bg-gold-light text-background font-semibold gap-2 h-12 px-8">
                  <Rocket className="w-5 h-5" />
                  Start Creating with AI
                </Button>
              </Link>
              <Link href="/games">
                <Button size="lg" variant="outline" className="h-12 px-8 border-border/50 hover:border-gold/50 hover:text-gold gap-2">
                  See Live Games
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Revenue Calculator */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Your <span className="text-gradient-gold">Creator Revenue</span> Potential
          </h2>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">
            As a game host, you earn a percentage of every entry fee. Here's what that looks like.
          </p>

          <div className="glass-panel rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Games Per Month</span>
                    <span className="text-gold font-mono">{monthlyGames}</span>
                  </label>
                  <input type="range" min={1} max={20} value={monthlyGames} onChange={(e) => setMonthlyGames(Number(e.target.value))} className="w-full accent-[oklch(0.80_0.16_85)]" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Average Players Per Game</span>
                    <span className="text-gold font-mono">{avgPlayers}</span>
                  </label>
                  <input type="range" min={10} max={2000} step={10} value={avgPlayers} onChange={(e) => setAvgPlayers(Number(e.target.value))} className="w-full accent-[oklch(0.80_0.16_85)]" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Average Entry Fee</span>
                    <span className="text-gold font-mono">${avgFee}</span>
                  </label>
                  <input type="range" min={1} max={100} value={avgFee} onChange={(e) => setAvgFee(Number(e.target.value))} className="w-full accent-[oklch(0.80_0.16_85)]" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 flex justify-between">
                    <span>Your Host Cut</span>
                    <span className="text-gold font-mono">{hostPct}%</span>
                  </label>
                  <input type="range" min={1} max={15} value={hostPct} onChange={(e) => setHostPct(Number(e.target.value))} className="w-full accent-[oklch(0.80_0.16_85)]" />
                </div>
              </div>

              <div className="flex flex-col justify-center space-y-4">
                <div className="glass-panel rounded-xl p-4">
                  <p className="text-sm text-muted-foreground">Per Game</p>
                  <p className="text-2xl font-bold font-mono text-gold">${revenue.perGame.toLocaleString()}</p>
                </div>
                <div className="glass-panel rounded-xl p-4 border border-gold/20">
                  <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                  <p className="text-3xl font-bold font-mono text-gradient-gold">${revenue.monthly.toLocaleString()}</p>
                </div>
                <div className="glass-panel rounded-xl p-4 border border-neon/20">
                  <p className="text-sm text-muted-foreground">Yearly Revenue</p>
                  <p className="text-3xl font-bold font-mono text-gradient-neon">${revenue.yearly.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Your AI <span className="text-gradient-neon">Game Design</span> Partner
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Our AI advisor doesn't just help — it does the heavy lifting. Describe your audience
            and the AI designs the game, validates compliance, and generates marketing materials.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiFeatures.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-panel rounded-xl p-6 hover:border-gold/30 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <f.icon className="w-5 h-5 text-gold" />
                </div>
                <h3 className="text-base font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Examples */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Real-World <span className="text-gradient-gold">Creator Success</span> Stories
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            From food YouTubers to coding bootcamps, nonprofit fundraisers to corporate HR —
            creators everywhere are building competitions that engage audiences and generate revenue.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {realWorldExamples.map((ex, i) => (
              <motion.div
                key={ex.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-panel rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg ${ex.color === "text-gold" ? "bg-gold/10" : "bg-neon/10"} flex items-center justify-center`}>
                    <ex.icon className={`w-5 h-5 ${ex.color}`} />
                  </div>
                  <h3 className="font-semibold">{ex.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{ex.scenario}</p>
                <p className="text-sm font-mono text-foreground/80 mb-2 bg-background/50 rounded-lg px-3 py-2">{ex.numbers}</p>
                <p className="text-sm text-neon font-medium">{ex.takeaway}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Create on GamePoint */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Create on <span className="text-gradient-gold">GamePoint</span>?
              </h2>
              <ul className="space-y-3">
                {creatorBenefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                    <span className="text-foreground/80">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <div className="glass-panel rounded-xl p-6 border border-gold/20">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-gold" />
                  <h3 className="font-semibold">10 Minutes to Launch</h3>
                </div>
                <p className="text-sm text-muted-foreground">From "I have an idea" to "my game is live" in under 10 minutes. The AI handles game design, rules, prize structure, compliance, and marketing materials.</p>
              </div>
              <div className="glass-panel rounded-xl p-6 border border-neon/20">
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="w-5 h-5 text-neon" />
                  <h3 className="font-semibold">Global Reach</h3>
                </div>
                <p className="text-sm text-muted-foreground">Your competition is open to the world. A cooking teacher in Tokyo can host a challenge that attracts entries from New York, Lagos, and Sao Paulo.</p>
              </div>
              <div className="glass-panel rounded-xl p-6 border border-gold/20">
                <div className="flex items-center gap-3 mb-3">
                  <Star className="w-5 h-5 text-gold" />
                  <h3 className="font-semibold">Build Your Brand</h3>
                </div>
                <p className="text-sm text-muted-foreground">Every game you host builds your reputation. Top creators get featured on the platform, attracting even more players to their competitions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-3xl mx-auto text-center glass-panel rounded-2xl p-12 glow-gold">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Your Audience Is Waiting to <span className="text-gradient-gold">Compete</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
            You already have the audience and the expertise. GamePoint's AI turns that into
            a revenue-generating competition in minutes. No coding. No complex setup. Just results.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/create">
              <Button size="lg" className="bg-gold hover:bg-gold-light text-background font-semibold gap-2 h-12 px-8">
                <Rocket className="w-5 h-5" />
                Create Your First Game
              </Button>
            </Link>
            <Link href="/games">
              <Button size="lg" variant="outline" className="h-12 px-8 border-gold/30 hover:border-gold text-gold gap-2">
                <Play className="w-5 h-5" />
                See What Others Built
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
